import PageShell from "@/components/site/PageShell";
import SEO from "@/components/site/SEO";
import JsonLd, { buildBreadcrumbs } from "@/components/site/JsonLd";
import TrustBand from "@/components/site/TrustBand";
import { Link } from "react-router-dom";
import { Briefcase, GraduationCap, Moon, ArrowRight, CheckCircle2 } from "lucide-react";
import { whatsappHref } from "@/lib/format";

const SECTIONS = [
  {
    icon: Briefcase,
    kicker: "Empresarial",
    title: "Rutas empresariales — transporte de personal",
    desc: "Recogida y entrega diaria de tus colaboradores en turnos rotativos, con puntualidad y respaldo administrativo.",
    points: [
      "Turnos diurnos, mixtos y rotativos",
      "Facturación empresarial mensual",
      "Cobertura Bogotá y municipios cercanos",
      "Reportes de llegada y salida por conductor",
    ],
  },
  {
    icon: GraduationCap,
    kicker: "Escolar",
    title: "Rutas escolares — seguridad y puntualidad",
    desc: "Servicio dedicado para colegios, jardines y universidades. Vehículos con cinturones, conductor certificado y protocolo de seguridad infantil.",
    points: [
      "Vehículos con cinturones de 3 puntos",
      "Conductores con curso pedagógico y antecedentes verificados",
      "Ruta con acompañante opcional",
      "Verificación del acudiente autorizado en cada entrega",
      "Trazabilidad para padres vía GPS en tiempo real",
    ],
  },
  {
    icon: Moon,
    kicker: "Nocturna",
    title: "Rutas nocturnas — turnos y personal 24/7",
    desc: "Traslados nocturnos para call centers, salud, seguridad, hoteles y empresas con turnos 24/7. Máxima seguridad y control.",
    points: [
      "Cobertura de 8 p.m. a 6 a.m.",
      "Protocolo de vía segura",
      "Reporte GPS del vehículo",
      "Facturación consolidada",
    ],
  },
];

export default function Rutas() {
  return (
    <PageShell testid="page-rutas">
      <SEO
        title="Rutas Empresariales, Escolares y Nocturnas | Líneas Doradas"
        description="Rutas fijas para empresas, colegios y turnos nocturnos en Bogotá. Transporte confiable y puntual para grupos, con conductores certificados."
      />
      <JsonLd
        id="breadcrumb-jsonld"
        data={buildBreadcrumbs([
          { name: "Inicio", url: "https://www.transporteenvanesybuses.com/" },
          { name: "Servicios", url: "https://www.transporteenvanesybuses.com/servicios/rutas" },
          { name: "Rutas", url: "https://www.transporteenvanesybuses.com/servicios/rutas" },
        ])}
      />
      <JsonLd
        id="service-jsonld"
        data={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Rutas empresariales, escolares y nocturnas",
          "serviceType": "Transporte de personal y rutas fijas",
          "provider": { "@id": "https://www.transporteenvanesybuses.com/#organization" },
          "areaServed": { "@type": "City", "name": "Bogotá" },
          "url": "https://www.transporteenvanesybuses.com/servicios/rutas",
          "description": "Rutas fijas para empresas, colegios y turnos nocturnos en Bogotá, con contratos mensuales y conductores certificados.",
        }}
      />

      <section className="relative pt-16 md:pt-24 pb-16 md:pb-20">
        <div className="orb-gold w-[520px] h-[520px] left-[-160px] top-[100px] opacity-70" />
        <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
          <div className="text-[#E8C87A] text-xs uppercase tracking-[0.35em] mb-4">Servicio · 03</div>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.05] text-[#F5EFE0] max-w-4xl">
            Rutas <span className="italic text-gold-grad">empresariales</span>, escolares y nocturnas en Bogotá
          </h1>
          <p className="text-[#F5EFE0]/70 mt-5 max-w-2xl text-base md:text-lg leading-relaxed">
            Servicios fijos con contratos mensuales. Somos el aliado logístico de RRHH, coordinaciones escolares y operaciones nocturnas que necesitan un transporte estable y confiable.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/cotizar" className="btn-gold px-7 py-3 rounded-full text-sm">Cotizar ruta fija</Link>
            <a href={whatsappHref("573123616674", "Hola, quiero información sobre rutas empresariales / escolares / nocturnas")} target="_blank" rel="noreferrer" className="btn-ghost-gold px-7 py-3 rounded-full text-sm">
              Consultar por WhatsApp
            </a>
          </div>
        </div>
      </section>

      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-5 md:px-8 space-y-6">
          {SECTIONS.map((s) => {
            const I = s.icon;
            return (
              <article key={s.kicker} className="card-lux p-8 md:p-10 grid md:grid-cols-[auto_1fr] gap-8">
                <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/12 border border-[#D4AF37]/35 flex items-center justify-center shrink-0">
                  <I size={30} className="text-[#E8C87A]" />
                </div>
                <div>
                  <div className="text-[#E8C87A] text-xs uppercase tracking-[0.3em] mb-2">{s.kicker}</div>
                  <h2 className="font-display text-2xl md:text-3xl text-[#F5EFE0] mb-3">{s.title}</h2>
                  <p className="text-[#F5EFE0]/65 leading-relaxed mb-5">{s.desc}</p>
                  <ul className="grid sm:grid-cols-2 gap-2">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-sm text-[#F5EFE0]/80">
                        <CheckCircle2 size={16} className="text-[#E8C87A] mt-0.5 shrink-0" /> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <TrustBand />
    </PageShell>
  );
}
